import { z } from "zod";
import { combinedMeetConfig } from "./combined";
import { clientIdField, poolField, sideField, versionField } from "./common";
import { COMPETITOR_ENTRY_MARK_MAX } from "./competitors";
import { JUDGED_PANEL_MAX, JUDGED_PARTS_MAX, JUDGED_RULES } from "./judged-rules";
import { CRICKET_INNINGS_MAX, CRICKET_OVERS_MAX, CRICKET_RUNS_MAX, CRICKET_WICKETS_MAX } from "./cricket";
import { SET_RULE_CODES } from "./set-rules";
import {
	type CompetitorStatus,
	IRM_CODES,
	MEET_OUTCOMES,
	type MemberRole,
	type ResultFamily,
	SLOT_SOURCE_OUTCOMES,
	type SlotSourceOutcome,
} from "./vocab";

/**
 * Results — a meet's start list, its settings, the results, and each team's
 * line-up for the meet. Every OVR scoring form calls these four endpoints:
 *
 *   PUT   /meets/:id/competitors          the start list (replace-all)
 *   PUT   /meets/:id/settings             the meet's `config` and `detail`
 *   PATCH /meets/:id/results              save results (all-or-nothing)
 *   PUT   /meet-competitors/:id/lineup    who of the team plays this meet
 *
 * ── What this replaces ──────────────────────────────────────────────────────
 * The predecessor kept one free-form `results` JSON per row: "winner" had six
 * spellings and numbers were strings. A save replaced the whole blob, so every
 * form did its own read-merge-write; line-ups, relay legs and pairs lived at
 * eleven different JSON paths; and nothing stopped two operators overwriting
 * each other.
 *
 * ── The typed headline: every sport, the same columns ───────────────────────
 * Each slot (a `meet_competitors` row) carries
 *
 *   irm             an invalid-result mark from IRM_CODES — null means the result is valid
 *   result_value    ONE canonical number (units below)
 *   result_display  exactly what the scoreboard shows: "50.23", "8.45", "21–19 21–18"
 *   points          what this meet contributes to a phase table: match points, combined-event points
 *   rank            the place within THIS meet
 *   outcome         WIN / LOSS / DRAW / BYE
 *   records         record marks this result set: GR, NR
 *
 * Standings, medals and schedules read ONLY these. Everything sport-specific
 * goes in `detail`, whose shape is the result family's (below).
 *
 * ── Canonical units of `result_value` (by the event's `result_type`) ────────
 *
 *   TIME      milliseconds        50.23 s        →  50230
 *   DISTANCE  millimetres         8.45 m         →  8450
 *   WEIGHT    kilograms           184 kg         →  184
 *   POINTS    the number itself   13.533 points  →  13.533
 *   SCORE     the number itself   280 strokes    →  280
 *   NONE      no headline number: the meet is decided by `outcome`
 *
 * One unit per type, so a standings query can compare two heats without
 * knowing the sport, and `result_display` is free to say "1:52.04" or "8.45 m".
 * Marks inside an ATTEMPTS `detail` (attempt marks, bar heights, the
 * qualifying mark) use the same unit as the headline — millimetres for a
 * DISTANCE event — so the best attempt and `result_value` are the same number.
 *
 * ── Result families: the shape of `detail` and `config` ─────────────────────
 * Which family applies to a meet = the meet's `result_family` override, else
 * its event's (`events.result_family`). Once the meet holds results — a saved
 * result, any `detail` below, or a `config` — its effective family, type and
 * direction are frozen: `PATCH /events/:id` and `PATCH /meets/:id` answer 409
 * CONFLICT `{ field, in_use: "results" }`. A results, settings or line-up save
 * whose family changed after the API validated its payload writes nothing and
 * answers 409 STALE_VERSION `{ result_family: <the current family> }`: reload
 * the meet, the form depends on the family. Each family has four schemas:
 *
 *   result detail   `meet_competitors.detail`  — one slot's sport-specific result
 *   line-up detail  `meet_lineups.detail`      — one member in a slot's line-up
 *   meet config     `meets.config`             — the meet's format / settings
 *   meet detail     `meets.detail`             — facts about the meet as a whole
 *
 * | Family   | result `detail` | line-up `detail` | meet `config` | meet `detail` |
 * |----------|-----------------|------------------|---------------|---------------|
 * | TIMED    | `reaction_ms?` int ≥ 0 · `gap_ms?` int ≥ 0 · `splits?: [{ seq ≥ 1, distance_m?, time_ms ≥ 0, cumulative_ms?, hold_ms? }]` · `laps?` int · `speed_kmh?` · `laps_delta?` int ±20 · `sprints?: [{ seq, points, place? }]` · `rides?: [{ seq 1–3, won?, time_ms?, irm? }]` (≤ 3) · `rank_locked?: true` | `split_ms?`, `reaction_ms?` | `distance_m?`, `legs?` int 1–8, `combined?`, `ranking?: TIME \| PLACE \| POINTS` | `wind_mps?` (−20…20) |
 * | ATTEMPTS | `attempts?: [{ seq ≥ 1, mark?: number ≥ 0, result: "VALID" \| "FOUL" \| "PASS", wind_mps? }]` · `heights?: [{ height: number > 0, tries: ^[XO-]{1,3}$ }]` · `best_seq?` | — | `attempts_allowed?` int 1–12 · `heights?: number[]` · `qualifying_mark?` · `combined?` | `wind_mps?` |
 * | SETS     | `periods?: [{ seq ≥ 1, score: int ≥ 0, tiebreak?: int ≥ 0, label?: string ≤ 12 }]` · `extra?: [{ seq, score }]` (overtime) · `shootout?` int ≥ 0 · `serving?` boolean · `innings?: [{ seq 1–8, runs, wickets 0–10, balls, target?, overs?, closed? }]` · `no_result?` · `tie_break_win?` true (cricket) | `events?: [{ type: ^[A-Z_]{1,20}$, minute?: int 0–200, period?: int ≥ 1 }]` (goals, cards) · `stats?: [{ code: ^[a-z][a-z0-9_]{0,29}$, value: number }]` | `best_of?` int 1–25 · `periods?` int 1–40 · `period_minutes?` · `race_to?` 1–30 · `rule?` · `play_all?` · `overs?` 1–50 | `durations?: [{ seq, seconds ≥ 0 }]` · `total_seconds?` |
 * | BOUT     | `rounds?: [{ seq ≥ 1, score?: number, penalties?: int ≥ 0 }]` · `judges?: [{ seat: string ≤ 8, scores?: number[] (≤ 12), total?: number }]` · `win_by?: ^[A-Z0-9_-]{1,12}$` · `tallies?: [{ code ^[a-z][a-z0-9_]{0,29}$, value: number }]` (ippon, waza_ari…) · `penalties?` int | — | `rounds?` int 1–12 · `round_seconds?` | `durations?` as SETS |
 * | JUDGED   | `parts?: [{ code: ^[A-Z0-9_.-]{1,12}$, label?: string ≤ 60, difficulty?: number, components?: [{ code ^[a-z][a-z0-9_]{0,29}$, value: number }], judges?: [{ seat: string ≤ 8, value: number, dropped?: true }], penalty?: number, total: number, rank?: int ≥ 1, dropped?: true }]` · `penalty?` · `tie_order?` int ≥ 1 | `parts?` with the same shape (a member's own routine) | `panel?: [{ seat: string ≤ 8, role?: string ≤ 20 }]` · `apparatus?: string[]` (≤ 12, ^[A-Z0-9_]{1,8}$) · `scoring?: { rule?, judges? 1–15, rounds? 1–5, counting? 1–12 }` | — |
 * | ROUNDS   | `rounds?: [{ seq ≥ 1, value: number, rank?: int ≥ 1, discarded?: boolean, irm?: one of IRM_CODES, parts?: [{ seq, value }] (≤ 36), points?, label? ≤ 12, factor?: 1 \| 2, keep?: true, decider?: true, time_ms? }]` · `tallies?: [{ code, value }]` · `rank_locked?: true` · `outcome_locked?: true` | `rounds?` same shape (a member's own rounds) | `rounds?` int 1–40 · `discards?` int ≥ 0 · `discard_steps?` · `medal_race?` · `round_word?` · `scoring?: SET \| CUMULATIVE` · `par?: int[]` · `best_of?` · `dec?` | — |
 * | LIFTS    | `lifts?: [{ type: "SNATCH" \| "CLEAN_JERK" \| "BENCH", seq 1–3, weight_kg: number > 0, good: boolean \| null, records?: string[] }]` (≤ 9) · `bodyweight_kg?` · `lot_no?` int · `entry_total_kg?` | — | — | — |
 * | GENERIC  | `lines?: [{ label: string ≤ 40, value: string ≤ 80 }]` (≤ 40) | `lines?` same | `lines?` same | `lines?` same |
 *
 * "—" means only `{}` is accepted. The worked examples are `RESULT_FAMILY_EXAMPLES`.
 *
 * ── Rules every family schema follows ───────────────────────────────────────
 *  · `.strict()`: an unknown key is a 422, never a silently stored typo.
 *  · Every key is optional (except inside array items), so `{}` is always
 *    valid: a slot with no detail yet passes.
 *  · Fixed snake_case keys; anything variable is an ARRAY of objects — never
 *    an object keyed by data, never `z.record`. The API's `ok()` runs
 *    `snakeCaseKeys()` over every response: a data-valued key such as "FX"
 *    would come back as "fx", and the key cache it fills would grow from user
 *    input. `tests/unit/result-families.test.ts` walks every schema and
 *    asserts every key is already snake_case.
 *  · Stored exactly as validated (no `.transform()` of keys); strings are
 *    trimmed and bounded, arrays bounded (≤ 64 unless the row says otherwise),
 *    numbers finite.
 */

// ─── Building blocks ─────────────────────────────────────────────────────────

/** The longest array any `detail` / `config` holds, unless its row in the table says otherwise. */
export const RESULT_FAMILY_ARRAY_MAX = 64;

/** A statistic, a tally or a judging component — `ippon`, `waza_ari`, `birdies`, `execution`. A code stored as a VALUE, so it can be lower-case. */
export const RESULT_STAT_CODE_PATTERN = /^[a-z][a-z0-9_]{0,29}$/;
/** What happened in a match, per player — GOAL, OWN_GOAL, YELLOW_CARD, RED_CARD. */
export const RESULT_MATCH_EVENT_PATTERN = /^[A-Z_]{1,20}$/;
/** The largest team statistic a match records — shots, fouls, a possession %. A typo of 10000 is refused. */
export const RESULT_TEAM_STAT_MAX = 9999;
/**
 * The team statistics OVR's match form asks for (`team_stats` codes), per
 * sport (`sports.code`), in its order — also what the public results feed
 * sends for that sport, in that order. A sport not listed records none.
 *
 *   FB   football      the match report's team figures; goals and cards are
 *                      counted from the match events, never typed
 *   BD   badminton     the longest run of points; the points won are the sum
 *                      of the game scores (never typed) and the game
 *                      durations are the meet's `detail.durations`
 *   STK  sepak takraw  how points were won (serve, spike, block) and lost on
 *                      serve; points won are the set scores' sum
 *
 * Weightlifting records none: its six attempts are its statistics.
 * Athletics records none here: wind and reaction times are the TIMED /
 * ATTEMPTS details.
 */
export const TEAM_STAT_CODES_BY_SPORT = {
	FB: ["possession", "shots", "shots_on_target", "corners", "fouls", "offsides", "saves"],
	BD: ["most_consecutive_points"],
	STK: ["service_aces", "service_faults", "spike_points", "block_points"],
} as const satisfies Readonly<Record<string, readonly string[]>>;
export type TeamStatSport = keyof typeof TEAM_STAT_CODES_BY_SPORT;
/** Every sport's codes, each once, in the sports' order above. */
export const TEAM_STAT_CODES = [...new Set(Object.values(TEAM_STAT_CODES_BY_SPORT).flat())] as unknown as readonly [TeamStatCode, ...TeamStatCode[]];
export type TeamStatCode = (typeof TEAM_STAT_CODES_BY_SPORT)[TeamStatSport][number];

/** Football's codes under their old name — kept so nothing that still imports it breaks. Prefer `teamStatCodesFor("FB")`. */
export const SETS_TEAM_STAT_CODES = TEAM_STAT_CODES_BY_SPORT.FB;
export type SetsTeamStatCode = (typeof SETS_TEAM_STAT_CODES)[number];

/**
 * The longest best-of a SETS meet takes: snooker finals are best of 11–19
 * frames, and pool's race to 9 is best of 17 when it is typed rack by rack.
 */
export const SETS_BEST_OF_MAX = 25;
/** The most regular periods: lawn bowls plays 15–21 ends, petanque up to ~25. */
export const SETS_PERIODS_MAX = 40;
/** "Race to N" (pool): the first to N racks wins. */
export const SETS_RACE_TO_MAX = 30;

/** The longest game or match OVR takes as typed (minutes) — a typo guard; the stored `durations` are seconds. */
export const SETS_DURATION_MINUTES_MAX = 300;

/** The statistics a sport's match form asks for (empty: none). */
export function teamStatCodesFor(sportCode: string | null | undefined): readonly TeamStatCode[] {
	return (sportCode && (TEAM_STAT_CODES_BY_SPORT as Readonly<Record<string, readonly TeamStatCode[]>>)[sportCode]) || [];
}

/**
 * Sports whose team statistics are kept per SET as well as for the whole
 * match (user, 2026-11-02: sepak takraw's Set 1, Set 2… and the match): a
 * `team_stats` entry with `period: n` is set n, without one the match. OVR's
 * form, TSR's mapper and the results feed read this one list.
 */
export const TEAM_STATS_BY_SET_SPORTS = ["STK"] as const satisfies readonly TeamStatSport[];

export const teamStatsBySet = (sportCode: string | null | undefined): boolean =>
	(TEAM_STATS_BY_SET_SPORTS as readonly string[]).includes((sportCode ?? "").trim().toUpperCase());

/**
 * A tail wind above this (m/s) makes a sprint or horizontal-jump mark
 * wind-assisted: it stands as a result, never as a record (World Athletics
 * Technical Rules; TR 32.21 for the jumps). OVR marks it, the public feed
 * flags it.
 */
export const WIND_ASSIST_LIMIT_MPS = 2.0;
/** The match events OVR logs on a line-up member (`detail.events[].type`); a substitution is a SUB_OUT and a SUB_IN at the same time. */
export const SETS_MATCH_EVENT_TYPES = ["GOAL", "PENALTY_GOAL", "OWN_GOAL", "YELLOW_CARD", "RED_CARD", "SUB_IN", "SUB_OUT"] as const;
export type SetsMatchEventType = (typeof SETS_MATCH_EVENT_TYPES)[number];
/** Stoppage time on a match event: the "+2" of 45+2'. */
export const MATCH_EVENT_ADDED_MAX = 30;
/**
 * Where a period match stands, live — the meet's `detail.live.phase` (SETS).
 * The clock runs in the halves and in extra time; the others stop it.
 */
export const MATCH_PHASES = ["FIRST_HALF", "HALF_TIME", "SECOND_HALF", "EXTRA_TIME", "PENALTIES", "FULL_TIME"] as const;
export type MatchPhase = (typeof MATCH_PHASES)[number];
/** How a bout was won — PTF (points), RSC, KO, IPPON, DSQ. */
export const RESULT_WIN_BY_PATTERN = /^[A-Z0-9_-]{1,12}$/;
/** The most a bout's `config.target` (the score that ends it) may be — a fencing team relay goes to 45. */
export const BOUT_TARGET_MAX = 99;
/** A judged part — an apparatus (FX), a dive number (107B), an element (D.1). */
export const RESULT_PART_CODE_PATTERN = /^[A-Z0-9_.-]{1,12}$/;
/** An apparatus on a judged meet's programme — FX, PH, SR, VT, PB, HB, UB, BB. */
export const RESULT_APPARATUS_PATTERN = /^[A-Z0-9_]{1,8}$/;
/** Tries at one bar height: X = failure, O = clearance, - = pass ("XO", "XXO", "-"). */
export const RESULT_HEIGHT_TRIES_PATTERN = /^[XO-]{1,3}$/;
/** A record mark a result set — GR (Games), NR (national), AR, WR. */
export const RESULT_RECORD_CODE_PATTERN = /^[A-Z]{2,4}$/;
/** Record marks on one result — and on one lift (a snatch can set a WR on its own). */
export const RESULT_RECORDS_MAX = 5;

/** One attempt in a jump or a throw: a valid mark, a foul, or a pass. */
export const ATTEMPT_RESULTS = ["VALID", "FOUL", "PASS"] as const;
export type AttemptResult = (typeof ATTEMPT_RESULTS)[number];

/** A lift: weightlifting's snatch and clean & jerk, para powerlifting's bench press. */
export const LIFT_TYPES = ["SNATCH", "CLEAN_JERK", "BENCH"] as const;
export type LiftType = (typeof LIFT_TYPES)[number];

/** Any finite number. `z.number()` already refuses NaN; `.finite()` refuses ±Infinity. */
const finite = () => z.number().finite();
const nonNegative = () => z.number().finite().min(0);
const positive = () => z.number().finite().positive();
/** `.int()` refuses NaN and ±Infinity too (`Number.isInteger`). */
const int = () => z.number().int();
const nonNegativeInt = () => z.number().int().min(0);
/** An order within a list — attempt 1, round 2, split 3. */
const seq = () => z.number().int().min(1);
/** Trimmed FIRST, then bounded — so "   " is refused, never stored as "". */
const text = (max: number) => z.string().trim().min(1).max(max);
const code = (pattern: RegExp) => z.string().trim().regex(pattern);
const list = <T extends z.ZodTypeAny>(item: T, max: number = RESULT_FAMILY_ARRAY_MAX) => z.array(item).max(max);
const strict = <T extends z.ZodRawShape>(shape: T) => z.object(shape).strict();

/** Metres per second, as a wind gauge reports it. ±20 covers every reading that is not a broken sensor. */
const windMps = () => z.number().finite().min(-20).max(20);
/** `{ code, value }` — a tally, a statistic or a judging component. An array of these, never `{ [code]: value }`. */
const statLine = () => strict({ code: code(RESULT_STAT_CODE_PATTERN), value: finite() });
/** How long each period / round actually lasted. */
const durations = () => list(strict({ seq: seq(), seconds: nonNegative() }));
/** GENERIC's label / value lines. */
const genericLines = () => list(strict({ label: text(40), value: text(80) }), 40);
/** For the "—" cells of the table: only `{}` is accepted. */
const nothing = () => strict({});

// ─── TIMED — swimming, track, cycling, rowing ────────────────────────────────

/**
 * How a TIMED race ranks (`config.ranking`):
 *   TIME    by `result_value` (the default — swimming, a time trial)
 *   PLACE   by the finishing place, typed as the rank (keirin, a sprint heat,
 *           a road race bunch, short track, BMX); the time is shown, never ranked
 *   POINTS  by `points`, higher first (a points race, a criterium on points)
 */
export const TIMED_RANKINGS = ["TIME", "PLACE", "POINTS"] as const;
export type TimedRanking = (typeof TIMED_RANKINGS)[number];
/** The most laps a rider gains or loses on the field (`laps_delta`). */
export const TIMED_LAPS_DELTA_MAX = 20;
/** A sprint match: ride 1, ride 2, the decider. */
export const SPRINT_RIDES_MAX = 3;
/** The most rounds a meet holds (`config.rounds`, a round's seq). */
export const ROUNDS_MAX = 40;

/**
 * TIMED result detail: `reaction_ms?` int ≥ 0 · `gap_ms?` int ≥ 0 ·
 * `splits?: [{ seq ≥ 1, distance_m?, time_ms ≥ 0, cumulative_ms? }]` ·
 * `laps?` int · `speed_kmh?`.
 *
 * The time itself is the headline: `result_value` in milliseconds,
 * `result_display` as the board shows it. Relay legs are line-up rows, not
 * splits here.
 */
const timedResultDetail = strict({
	reaction_ms: nonNegativeInt().optional(),
	/** Behind the leader. */
	gap_ms: nonNegativeInt().optional(),
	splits: list(
		strict({
			seq: seq(),
			distance_m: positive().optional(),
			/** This split's own time. */
			time_ms: nonNegativeInt(),
			/** Elapsed time at the end of this split. */
			cumulative_ms: nonNegativeInt().optional(),
			/** Time held at this point — an endurance vet gate's recovery hold. */
			hold_ms: nonNegativeInt().optional(),
		}),
	).optional(),
	laps: nonNegativeInt().optional(),
	speed_kmh: nonNegative().optional(),
	/**
	 * Laps gained (+) or lost (−) on the field: a points race's ±20 per lap,
	 * an MTB / road rider placed "−1 LAP". Signed, unlike `laps`.
	 */
	laps_delta: z.number().int().min(-TIMED_LAPS_DELTA_MAX).max(TIMED_LAPS_DELTA_MAX).optional(),
	/** A points race's intermediate sprints: the points won at each, and the place (the final sprint's place breaks a tie). */
	sprints: list(strict({ seq: seq(), points: nonNegative(), place: seq().optional() })).optional(),
	/**
	 * A sprint match (2 riders, best of three): each ride, `won` by this rider
	 * or not, the winner's time (the last 200 m) and a ride's own mark.
	 */
	rides: list(
		strict({ seq: z.number().int().min(1).max(SPRINT_RIDES_MAX), won: z.boolean().optional(), time_ms: nonNegativeInt().optional(), irm: z.enum(IRM_CODES).optional() }),
		SPRINT_RIDES_MAX,
	).optional(),
	/** The rank was typed (a finishing place, a decision): live ranking leaves it alone. */
	rank_locked: z.literal(true).optional(),
});

/**
 * TIMED line-up detail: `split_ms?`, `reaction_ms?` — one relay leg.
 *
 * `reaction_ms` is signed HERE: a relay take-off is timed against the
 * incoming swimmer's touch and may legally be early (down to −30 ms in
 * swimming), so a negative exchange is a real reading, not an error.
 */
const timedLineupDetail = strict({
	split_ms: nonNegativeInt().optional(),
	reaction_ms: int().optional(),
});

/** TIMED meet config: `distance_m?`, `legs?` int 1–8 (a relay), `combined?` (a decathlon / heptathlon race). */
const timedMeetConfig = strict({
	distance_m: positive().optional(),
	legs: z.number().int().min(1).max(8).optional(),
	combined: combinedMeetConfig.optional(),
	/** How the race ranks (contracts `timedRankingFor` gives the default): by time, by finishing place, by points. */
	ranking: z.enum(TIMED_RANKINGS).optional(),
});

/** TIMED meet detail: `wind_mps?` (−20…20) — the race's wind reading (outdoor sprints). */
const timedMeetDetail = strict({
	wind_mps: windMps().optional(),
});

// ─── ATTEMPTS — jumps, throws ────────────────────────────────────────────────

/**
 * ATTEMPTS result detail: `attempts?: [{ seq ≥ 1, mark?: number ≥ 0, result:
 * "VALID" | "FOUL" | "PASS", wind_mps? }]` · `heights?: [{ height: number > 0,
 * tries: ^[XO-]{1,3}$ }]` · `best_seq?`.
 *
 * Horizontal jumps and throws use `attempts`; the high jump and pole vault
 * use `heights`. Marks and heights are in the headline's unit (millimetres).
 */
const attemptsResultDetail = strict({
	attempts: list(
		strict({
			seq: seq(),
			mark: nonNegative().optional(),
			result: z.enum(ATTEMPT_RESULTS),
			wind_mps: windMps().optional(),
		}),
	).optional(),
	heights: list(
		strict({
			height: positive(),
			tries: code(RESULT_HEIGHT_TRIES_PATTERN),
		}),
	).optional(),
	/** Which attempt is the competitor's best — the one `result_value` repeats. */
	best_seq: seq().optional(),
});

/** ATTEMPTS line-up detail: — (only `{}`). */
const attemptsLineupDetail = nothing();

/** ATTEMPTS meet config: `attempts_allowed?` int 1–12 · `heights?: number[]` (the bar's progression) · `qualifying_mark?` · `combined?` (a decathlon / heptathlon jump or throw). */
const attemptsMeetConfig = strict({
	attempts_allowed: z.number().int().min(1).max(12).optional(),
	heights: list(positive()).optional(),
	qualifying_mark: nonNegative().optional(),
	combined: combinedMeetConfig.optional(),
});

/** ATTEMPTS meet detail: `wind_mps?`. */
const attemptsMeetDetail = strict({
	wind_mps: windMps().optional(),
});

// ─── SETS — racket, net and period sports ────────────────────────────────────

/**
 * SETS result detail: `periods?: [{ seq ≥ 1, score: int ≥ 0, tiebreak?: int ≥
 * 0, label?: string ≤ 12 }]` · `extra?: [{ seq, score }]` (overtime) ·
 * `shootout?` int ≥ 0 · `serving?` boolean · `team_stats?: [{ code:
 * ^[a-z][a-z0-9_]{0,29}$, value: int 0–9999, period?: int ≥ 1 }]` — the
 * team's match statistics (possession, shots, corners…), for one period or,
 * without `period`, the whole match.
 *
 * Each slot holds ITS OWN scores — one side's games in a badminton match, one
 * team's goals per half. Who won is the headline's `outcome`.
 */
const setsResultDetail = strict({
	periods: list(
		strict({
			seq: seq(),
			score: nonNegativeInt(),
			tiebreak: nonNegativeInt().optional(),
			/** What the board calls the period — "Q1", "1st half". */
			label: text(12).optional(),
		}),
	).optional(),
	/** Overtime / extra time. */
	extra: list(strict({ seq: seq(), score: nonNegativeInt() })).optional(),
	shootout: nonNegativeInt().optional(),
	serving: z.boolean().optional(),
	/**
	 * Cricket: this side's innings, `seq` = the innings' place in the MATCH
	 * (1 batted first, 2 the chase, 3 / 4 a super over) — `cricket.ts`.
	 */
	innings: list(
		strict({
			seq: z.number().int().min(1).max(CRICKET_INNINGS_MAX),
			runs: z.number().int().min(0).max(CRICKET_RUNS_MAX),
			wickets: z.number().int().min(0).max(CRICKET_WICKETS_MAX),
			/** Legal balls faced (shown as overs.balls). */
			balls: z.number().int().min(0).max(CRICKET_OVERS_MAX * 6),
			/** A revised target for a reduced chase (DLS): the runs the chase needs to win. */
			target: z.number().int().min(1).max(CRICKET_RUNS_MAX).optional(),
			/** The innings' own overs when reduced (rain); absent = the meet's `config.overs`. */
			overs: z.number().int().min(1).max(CRICKET_OVERS_MAX).optional(),
			/** The operator ended the innings (declared, stopped): it is over whatever the score. */
			closed: z.literal(true).optional(),
		}),
		CRICKET_INNINGS_MAX,
	).optional(),
	/** Cricket: abandoned, no result (set on both sides) — no outcome, the no-result points. */
	no_result: z.literal(true).optional(),
	/**
	 * Cricket: this side won a match still level after its last super over, by
	 * the competition's tie-break (boundary count, a further rule) — recorded
	 * by the operator; only read when every super over is complete and level.
	 */
	tie_break_win: z.literal(true).optional(),
	team_stats: list(
		strict({
			code: code(RESULT_STAT_CODE_PATTERN),
			value: z.number().int().min(0).max(RESULT_TEAM_STAT_MAX),
			period: seq().optional(),
		}),
	).optional(),
});

/**
 * SETS line-up detail: `events?: [{ type: ^[A-Z_]{1,20}$, minute?: int 0–200,
 * added?: int 1–30 (stoppage time: 45+2' is minute 45, added 2), period?:
 * int ≥ 1 }]` (goals, cards, substitutions) · `stats?: [{ code:
 * ^[a-z][a-z0-9_]{0,29}$, value: number }]`.
 *
 * A player's own match: the goals and cards replace the predecessor's
 * `goals` / `cards` blobs, which were keyed by player name.
 */
const setsLineupDetail = strict({
	events: list(
		strict({
			type: code(RESULT_MATCH_EVENT_PATTERN),
			minute: z.number().int().min(0).max(200).optional(),
			added: z.number().int().min(1).max(MATCH_EVENT_ADDED_MAX).optional(),
			period: seq().optional(),
		}),
	).optional(),
	stats: list(statLine()).optional(),
});

/**
 * SETS meet config: `best_of?` int 1–`SETS_BEST_OF_MAX` · `periods?` int
 * 1–`SETS_PERIODS_MAX` · `period_minutes?` · `race_to?` int
 * 1–`SETS_RACE_TO_MAX` (pool: the first to N racks; instead of `best_of` /
 * `periods`) · `rule?` a `SET_RULE_PRESETS` key (this meet's own set rule — a
 * tennis doubles rubber's match tie-break) · `play_all?` true (best-of: every
 * game is played and a level count is a DRAW — an eFootball duo group game)
 * · `overs?` int 1–`CRICKET_OVERS_MAX` (cricket: overs per innings).
 */
const setsMeetConfig = strict({
	best_of: z.number().int().min(1).max(SETS_BEST_OF_MAX).optional(),
	periods: z.number().int().min(1).max(SETS_PERIODS_MAX).optional(),
	period_minutes: positive().optional(),
	race_to: z.number().int().min(1).max(SETS_RACE_TO_MAX).optional(),
	rule: z.enum(SET_RULE_CODES).optional(),
	play_all: z.literal(true).optional(),
	overs: z.number().int().min(1).max(CRICKET_OVERS_MAX).optional(),
}).refine((c) => c.race_to === undefined || (c.best_of === undefined && c.periods === undefined), {
	message: "race_to replaces best_of and periods",
	path: ["race_to"],
});

/**
 * SETS meet detail: `durations?: [{ seq, seconds ≥ 0 }]` · `total_seconds?` ·
 * `live?: { phase: MATCH_PHASES, started_at?: ISO 8601 with offset }` — where
 * a period match stands and when that phase began (the OVR clock counts from
 * it). Not part of the result.
 */
const setsMeetDetail = strict({
	durations: durations().optional(),
	total_seconds: nonNegative().optional(),
	live: strict({
		phase: z.enum(MATCH_PHASES),
		/** ISO 8601 with an offset — "2026-10-12T16:04:00.000+08:00" is 29 characters. */
		started_at: z.string().trim().max(40).datetime({ offset: true }).optional(),
	}).optional(),
});

// ─── BOUT — combat sports ────────────────────────────────────────────────────

/**
 * BOUT result detail: `rounds?: [{ seq ≥ 1, score?: number, penalties?: int ≥
 * 0 }]` · `judges?: [{ seat: string ≤ 8, scores?: number[] (≤ 12), total?:
 * number }]` · `win_by?: ^[A-Z0-9_-]{1,12}$` · `tallies?: [{ code
 * ^[a-z][a-z0-9_]{0,29}$, value: number }]` (ippon, waza_ari…) · `penalties?` int.
 *
 * `judges` is a boxing / silat card: one entry per seat, never `{ "J1": … }`.
 *
 * A round may also carry `win_by?` (same pattern as the bout's) — on the
 * round WINNER's entry only: how a round-by-round sport decided that round
 * (taekwondo PTF / PTG / SUP; wushu's tie-break). It names the round's winner
 * even on a level score (`boutRoundWinner`). And `member_id?` — the line-up
 * member (`competitor_member_id`) who fought it: a fencing team relay's
 * fencer for that leg.
 */
const boutResultDetail = strict({
	rounds: list(
		strict({
			seq: seq(),
			score: finite().optional(),
			penalties: nonNegativeInt().optional(),
			win_by: code(RESULT_WIN_BY_PATTERN).optional(),
			member_id: z.string().trim().max(36).uuid().toLowerCase().optional(),
		}),
	).optional(),
	judges: list(
		strict({
			seat: text(8),
			scores: list(finite(), 12).optional(),
			total: finite().optional(),
		}),
	).optional(),
	win_by: code(RESULT_WIN_BY_PATTERN).optional(),
	tallies: list(statLine()).optional(),
	penalties: nonNegativeInt().optional(),
});

/** BOUT line-up detail: — (only `{}`). */
const boutLineupDetail = nothing();

/**
 * BOUT meet config: `rounds?` int 1–12 · `round_seconds?` · `target?` int
 * 1–`BOUT_TARGET_MAX` — the score that ends the bout (fencing: 5 a pool
 * bout, 15 a direct elimination, 45 a team relay): a corner that reaches it
 * wins, whatever is left of the time.
 */
const boutMeetConfig = strict({
	rounds: z.number().int().min(1).max(12).optional(),
	round_seconds: positive().optional(),
	target: z.number().int().min(1).max(BOUT_TARGET_MAX).optional(),
});

/** BOUT meet detail: `durations?` — as SETS, `[{ seq, seconds ≥ 0 }]`. */
const boutMeetDetail = strict({
	durations: durations().optional(),
});

// ─── JUDGED — gymnastics, diving, taolu, kata, silat seni ────────────────────

/** One judged part: an apparatus, a dive, a routine. Shared by the result and the line-up detail. */
const judgedPart = () =>
	strict({
		code: code(RESULT_PART_CODE_PATTERN),
		label: text(60).optional(),
		difficulty: finite().optional(),
		components: list(statLine()).optional(),
		/** `dropped`: a mark the rule left out (the high / low of a trimmed panel), shown struck through. */
		judges: list(strict({ seat: text(8), value: finite(), dropped: z.literal(true).optional() })).optional(),
		penalty: finite().optional(),
		total: finite(),
		rank: seq().optional(),
		/** Not counted in the result — a team's worst rider, like ROUNDS' `discarded`. */
		dropped: z.literal(true).optional(),
	});

/**
 * JUDGED result detail: `parts?: [{ code: ^[A-Z0-9_.-]{1,12}$, label?: string
 * ≤ 60, difficulty?: number, components?: [{ code ^[a-z][a-z0-9_]{0,29}$,
 * value: number }], judges?: [{ seat: string ≤ 8, value: number, dropped?:
 * true }], penalty?: number, total: number, rank?: int ≥ 1, dropped?: true }]`
 * · `penalty?` · `tie_order?: int ≥ 1`.
 *
 * The apparatus is a VALUE (`code: "FX"`), never a key: `{ "FX": … }` would
 * reach the client as `{ "fx": … }` after `ok()`. What the operator types and
 * what the sport's rule computes both live here (contracts `judged.ts`).
 */
const judgedResultDetail = strict({
	parts: list(judgedPart()).optional(),
	penalty: finite().optional(),
	/**
	 * The operator's order among rows the rule's tie-breaks leave level (1 =
	 * ahead) — the last tie-break, so a decided tie stays decided when the
	 * ranks are computed again. Absent = not decided (the rows share the rank).
	 */
	tie_order: seq().optional(),
});

/** JUDGED line-up detail: `parts?` with the same shape — a member's own routine in a team event. */
const judgedLineupDetail = strict({
	parts: list(judgedPart()).optional(),
});

/**
 * JUDGED meet config: `panel?: [{ seat: string ≤ 8, role?: string ≤ 20 }]` ·
 * `apparatus?: string[]` (≤ 12, ^[A-Z0-9_]{1,8}$ — the parts this meet
 * scores, in order: apparatus, dances, segments) · `scoring?: { rule?:
 * JUDGED_RULES, judges?: int 1–15, rounds?: int 1–5, counting?: int 1–12 }` —
 * the meet-level choices only (how many judges sit, battle rounds, riders
 * counted); the formula itself is the rule's (contracts `judged.ts`).
 */
const judgedMeetConfig = strict({
	panel: list(strict({ seat: text(8), role: text(20).optional() })).optional(),
	apparatus: list(code(RESULT_APPARATUS_PATTERN), 12).optional(),
	scoring: strict({
		rule: z.enum(JUDGED_RULES).optional(),
		judges: z.number().int().min(1).max(JUDGED_PANEL_MAX).optional(),
		rounds: z.number().int().min(1).max(5).optional(),
		counting: z.number().int().min(1).max(JUDGED_PARTS_MAX).optional(),
	}).optional(),
});

/** JUDGED meet detail: — (only `{}`). */
const judgedMeetDetail = nothing();

// ─── ROUNDS — golf, bowling, sailing, shooting and archery series, chess ─────

/**
 * What a sport calls one round on its sheet (`config.round_word`; contracts
 * `roundsRulesFor` gives the sport's default): an archery END, a shooting
 * SERIES, a golf ROUND, a sailing RACE, a bowling GAME, a RUN, a BMX MOTO, a
 * water-ski PASS.
 */
export const ROUND_WORDS = ["end", "series", "round", "race", "game", "run", "moto", "pass"] as const;
export type RoundWord = (typeof ROUND_WORDS)[number];
/** An archery head-to-head: SET play (2 / 1 / 0 per end, first to 6 — recurve) or CUMULATIVE (the arrow total — compound). */
export const ROUNDS_SCORINGS = ["SET", "CUMULATIVE"] as const;
export type RoundsScoring = (typeof ROUNDS_SCORINGS)[number];
/** The longest round label ("70m-1", "K1", "MR", "JO", "58/11.25"). */
export const ROUND_LABEL_MAX = 12;
/** Holes, ends, frames or shots inside one round. */
export const ROUND_PARTS_MAX = 36;

/** One round of a multi-round result. Shared by the result and the line-up detail. */
const roundsRound = () =>
	strict({
		seq: seq(),
		value: finite(),
		rank: seq().optional(),
		/** Dropped from the total — a sailing discard, a team's worst score. */
		discarded: z.boolean().optional(),
		irm: z.enum(IRM_CODES).optional(),
		/** Holes, ends, frames, shots. */
		parts: list(strict({ seq: seq(), value: finite() }), ROUND_PARTS_MAX).optional(),
		/** What this round earns besides its value: archery set points (2 / 1 / 0), a bowling match bonus. */
		points: finite().optional(),
		/** The round's name on the sheet: "70m-1", "K1", "MR" (medal race), "JO" (jump-off), "58/12" (slalom speed / line). */
		label: text(ROUND_LABEL_MAX).optional(),
		/** Counts double — a sailing medal race (World Sailing, Appendix A medal race scoring). */
		factor: z.union([z.literal(1), z.literal(2)]).optional(),
		/** Never discarded — a sailing medal race. */
		keep: z.literal(true).optional(),
		/** A decider (a jump-off, a play-off, a shoot-off): never in the total, it only breaks a tie. */
		decider: z.literal(true).optional(),
		/** The round's time, ms — an equestrian round (the jump-off time breaks a tie). */
		time_ms: nonNegativeInt().optional(),
	});

/**
 * ROUNDS result detail: `rounds?: [{ seq ≥ 1, value: number, rank?: int ≥ 1,
 * discarded?: boolean, irm?: one of IRM_CODES, parts?: [{ seq, value }] (≤ 36),
 * points?, label?, factor?: 1 | 2, keep?: true, decider?: true, time_ms? }]` ·
 * `tallies?: [{ code, value }]` · `rank_locked?: true` · `outcome_locked?: true`.
 */
const roundsResultDetail = strict({
	rounds: list(roundsRound()).optional(),
	tallies: list(statLine()).optional(),
	/** The rank was typed (a shoot-off, a play-off, a countback the sheet decides): OVR's live ranking leaves it alone. */
	rank_locked: z.literal(true).optional(),
	/** The winner was picked by hand (closest to centre, a roll-off): OVR does not re-derive the outcome. */
	outcome_locked: z.literal(true).optional(),
});

/** ROUNDS line-up detail: `rounds?` — same shape, a member's own rounds in a team event. */
const roundsLineupDetail = strict({
	rounds: list(roundsRound()).optional(),
});

/**
 * ROUNDS meet config: `rounds?` int 1–40 · `discards?` int ≥ 0 ·
 * `discard_steps?: [{ from, discards }]` · `medal_race?` · `round_word?` ·
 * `scoring?` · `par?: int[]` · `best_of?` · `dec?`.
 */
const roundsMeetConfig = strict({
	rounds: z.number().int().min(1).max(ROUNDS_MAX).optional(),
	discards: nonNegativeInt().optional(),
	/**
	 * Discards by races sailed (sailing): from `from` races counted, drop
	 * `discards`. Used when `discards` is not set; the step with the highest
	 * `from` that the series has reached applies.
	 */
	discard_steps: list(strict({ from: z.number().int().min(1).max(40), discards: z.number().int().min(0).max(10) }), 8).optional(),
	/** The meet's LAST round (seq = `rounds`) is a medal race: it counts double and is never discarded. */
	medal_race: z.boolean().optional(),
	round_word: z.enum(ROUND_WORDS).optional(),
	/** Archery head-to-head: set play or the arrow total. */
	scoring: z.enum(ROUNDS_SCORINGS).optional(),
	/** Par per hole (golf), in hole order — the course card. */
	par: list(z.number().int().min(1).max(9), ROUND_PARTS_MAX).optional(),
	/** A team's round counts its best N members (golf 3 of 4, equestrian 3 of 4). */
	best_of: z.number().int().min(1).max(10).optional(),
	/** Decimals a round is typed with (shooting 10 m rifle 1, eventing dressage 1). */
	dec: z.number().int().min(0).max(3).optional(),
});

/** ROUNDS meet detail: — (only `{}`). */
const roundsMeetDetail = nothing();

// ─── LIFTS — weightlifting, powerlifting ─────────────────────────────────────

/**
 * LIFTS result detail: `lifts?: [{ type: "SNATCH" | "CLEAN_JERK" | "BENCH",
 * seq 1–3, weight_kg: number > 0, good: boolean | null, records?: string[] }]`
 * (≤ 9) · `bodyweight_kg?` · `lot_no?` int · `entry_total_kg?`.
 *
 * `good: null` = declared, not lifted yet. `records`: marks THIS lift set
 * (WR, AR, GR); the total's own marks are the row's `records`. The total is
 * the headline (`result_value` in kilograms). Weightlifting's lot number,
 * bodyweight and entry total now live on the slot (start list: `bib`,
 * `bodyweight_kg`, `entry_mark`); the detail keys stay for imported results.
 */
const liftsResultDetail = strict({
	lifts: list(
		strict({
			type: z.enum(LIFT_TYPES),
			seq: z.number().int().min(1).max(3),
			weight_kg: positive(),
			good: z.boolean().nullable(),
			records: list(code(RESULT_RECORD_CODE_PATTERN), RESULT_RECORDS_MAX).optional(),
		}),
		9,
	).optional(),
	bodyweight_kg: positive().optional(),
	lot_no: seq().optional(),
	entry_total_kg: positive().optional(),
});

/** LIFTS line-up detail: — (only `{}`). */
const liftsLineupDetail = nothing();
/** LIFTS meet config: — (only `{}`). */
const liftsMeetConfig = nothing();
/** LIFTS meet detail: — (only `{}`). */
const liftsMeetDetail = nothing();

// ─── GENERIC — anything not modelled yet ─────────────────────────────────────

/**
 * GENERIC, all four: `lines?: [{ label: string ≤ 40, value: string ≤ 80 }]`
 * (≤ 40). Label / value text, so no sport is blocked on day one; a sport that
 * outgrows it gets a family of its own.
 */
const genericDetail = strict({
	lines: genericLines().optional(),
});

// ─── The registry and its lookups ────────────────────────────────────────────

interface FamilySchemaSet {
	result: z.ZodTypeAny;
	lineup: z.ZodTypeAny;
	config: z.ZodTypeAny;
	meetDetail: z.ZodTypeAny;
}

/**
 * Every family's four schemas. A `satisfies Record<ResultFamily, …>`: adding a
 * family to `RESULT_FAMILIES` fails typecheck here until it has its schemas.
 */
export const RESULT_FAMILY_SCHEMAS = {
	TIMED: { result: timedResultDetail, lineup: timedLineupDetail, config: timedMeetConfig, meetDetail: timedMeetDetail },
	ATTEMPTS: { result: attemptsResultDetail, lineup: attemptsLineupDetail, config: attemptsMeetConfig, meetDetail: attemptsMeetDetail },
	SETS: { result: setsResultDetail, lineup: setsLineupDetail, config: setsMeetConfig, meetDetail: setsMeetDetail },
	BOUT: { result: boutResultDetail, lineup: boutLineupDetail, config: boutMeetConfig, meetDetail: boutMeetDetail },
	JUDGED: { result: judgedResultDetail, lineup: judgedLineupDetail, config: judgedMeetConfig, meetDetail: judgedMeetDetail },
	ROUNDS: { result: roundsResultDetail, lineup: roundsLineupDetail, config: roundsMeetConfig, meetDetail: roundsMeetDetail },
	LIFTS: { result: liftsResultDetail, lineup: liftsLineupDetail, config: liftsMeetConfig, meetDetail: liftsMeetDetail },
	GENERIC: { result: genericDetail, lineup: genericDetail, config: genericDetail, meetDetail: genericDetail },
} satisfies Record<ResultFamily, FamilySchemaSet>;

type FamilySchemas = typeof RESULT_FAMILY_SCHEMAS;

/** A slot's result `detail` (`meet_competitors.detail`) for a family. */
export const resultDetailSchema = <F extends ResultFamily>(family: F): FamilySchemas[F]["result"] => RESULT_FAMILY_SCHEMAS[family].result;
/** One line-up member's `detail` (`meet_lineups.detail`) for a family. */
export const lineupDetailSchema = <F extends ResultFamily>(family: F): FamilySchemas[F]["lineup"] => RESULT_FAMILY_SCHEMAS[family].lineup;
/** A meet's `config` (`meets.config`) for a family. */
export const meetConfigSchema = <F extends ResultFamily>(family: F): FamilySchemas[F]["config"] => RESULT_FAMILY_SCHEMAS[family].config;
/** A meet's `detail` (`meets.detail`) for a family. */
export const meetDetailSchema = <F extends ResultFamily>(family: F): FamilySchemas[F]["meetDetail"] => RESULT_FAMILY_SCHEMAS[family].meetDetail;

/** A slot's result `detail`. `FamilyResultDetail<"TIMED">` for one family; the union of all by default. */
export type FamilyResultDetail<F extends ResultFamily = ResultFamily> = z.infer<FamilySchemas[F]["result"]>;
/** One line-up member's `detail`. */
export type FamilyLineupDetail<F extends ResultFamily = ResultFamily> = z.infer<FamilySchemas[F]["lineup"]>;
/** A meet's `config`. */
export type FamilyMeetConfig<F extends ResultFamily = ResultFamily> = z.infer<FamilySchemas[F]["config"]>;
/** A meet's `detail`. */
export type FamilyMeetDetail<F extends ResultFamily = ResultFamily> = z.infer<FamilySchemas[F]["meetDetail"]>;

export type TimedResultDetail = FamilyResultDetail<"TIMED">;
export type TimedLineupDetail = FamilyLineupDetail<"TIMED">;
export type TimedMeetConfig = FamilyMeetConfig<"TIMED">;
export type TimedMeetDetail = FamilyMeetDetail<"TIMED">;
export type AttemptsResultDetail = FamilyResultDetail<"ATTEMPTS">;
export type AttemptsMeetConfig = FamilyMeetConfig<"ATTEMPTS">;
export type AttemptsMeetDetail = FamilyMeetDetail<"ATTEMPTS">;
export type SetsResultDetail = FamilyResultDetail<"SETS">;
export type SetsLineupDetail = FamilyLineupDetail<"SETS">;
export type SetsMeetConfig = FamilyMeetConfig<"SETS">;
export type SetsMeetDetail = FamilyMeetDetail<"SETS">;
export type BoutResultDetail = FamilyResultDetail<"BOUT">;
export type BoutMeetConfig = FamilyMeetConfig<"BOUT">;
export type BoutMeetDetail = FamilyMeetDetail<"BOUT">;
export type JudgedResultDetail = FamilyResultDetail<"JUDGED">;
export type JudgedLineupDetail = FamilyLineupDetail<"JUDGED">;
export type JudgedMeetConfig = FamilyMeetConfig<"JUDGED">;
export type RoundsResultDetail = FamilyResultDetail<"ROUNDS">;
export type RoundsLineupDetail = FamilyLineupDetail<"ROUNDS">;
export type RoundsMeetConfig = FamilyMeetConfig<"ROUNDS">;
export type LiftsResultDetail = FamilyResultDetail<"LIFTS">;
export type GenericFamilyDetail = FamilyResultDetail<"GENERIC">;

interface FamilyExample<F extends ResultFamily> {
	result: FamilyResultDetail<F>;
	lineup: FamilyLineupDetail<F>;
	config: FamilyMeetConfig<F>;
	meetDetail: FamilyMeetDetail<F>;
}

/**
 * One real example per family, for each of its four schemas — the reference
 * an OVR developer copies from. `tests/unit/result-families.test.ts` parses
 * every one of them with its schema, so an example can never drift from the
 * rule it illustrates.
 */
export const RESULT_FAMILY_EXAMPLES = {
	/**
	 * Swimming, Men's 100 m freestyle, heat 2, lane 4: 49.23 → `result_value`
	 * 49230, `result_display` "49.23". The line-up example is the second leg of
	 * a 4×100 m relay (a 0.21 s exchange). Swimming has no wind: the meet
	 * detail is an athletics 100 m heat's reading.
	 */
	TIMED: {
		result: {
			reaction_ms: 620,
			splits: [
				{ seq: 1, distance_m: 50, time_ms: 23410, cumulative_ms: 23410 },
				{ seq: 2, distance_m: 100, time_ms: 25820, cumulative_ms: 49230 },
			],
		},
		lineup: { split_ms: 48870, reaction_ms: 210 },
		config: { distance_m: 100 },
		meetDetail: { wind_mps: -0.4 },
	},
	/**
	 * Athletics, Men's long jump final: best 8.12 m in round 3 → `result_value`
	 * 8120, `result_display` "8.12". Marks in millimetres, like the headline.
	 */
	ATTEMPTS: {
		result: {
			attempts: [
				{ seq: 1, mark: 7980, result: "VALID", wind_mps: 1.1 },
				{ seq: 2, result: "FOUL", wind_mps: 0.4 },
				{ seq: 3, mark: 8120, result: "VALID", wind_mps: 1.8 },
			],
			best_seq: 3,
		},
		lineup: {},
		config: { attempts_allowed: 6, qualifying_mark: 7900 },
		meetDetail: { wind_mps: 0.6 },
	},
	/**
	 * Badminton, Men's singles semi-final, one side: 21–17 19–21 21–18 →
	 * `outcome` WIN, `result_display` "21–17 19–21 21–18". The line-up
	 * example is a football player's match (badminton has no goals or cards).
	 */
	SETS: {
		result: {
			periods: [
				{ seq: 1, score: 21 },
				{ seq: 2, score: 19 },
				{ seq: 3, score: 21 },
			],
			serving: false,
			team_stats: [
				{ code: "possession", value: 54, period: 1 },
				{ code: "possession", value: 52 },
			],
		},
		lineup: {
			events: [
				{ type: "GOAL", minute: 67, period: 2 },
				{ type: "YELLOW_CARD", minute: 90, added: 3, period: 2 },
			],
			stats: [{ code: "shots_on_target", value: 3 }],
		},
		config: { best_of: 3 },
		meetDetail: {
			durations: [
				{ seq: 1, seconds: 1260 },
				{ seq: 2, seconds: 1320 },
				{ seq: 3, seconds: 1500 },
			],
			total_seconds: 4080,
		},
	},
	/**
	 * Taekwondo, Women's −57 kg quarter-final, the winner: two rounds won on
	 * points → `outcome` WIN, `win_by` "PTF".
	 */
	BOUT: {
		result: {
			rounds: [
				{ seq: 1, score: 7, penalties: 1 },
				{ seq: 2, score: 12, penalties: 0 },
			],
			win_by: "PTF",
			tallies: [
				{ code: "head_kicks", value: 3 },
				{ code: "body_kicks", value: 4 },
			],
			penalties: 1,
		},
		lineup: {},
		config: { rounds: 3, round_seconds: 120 },
		meetDetail: {
			durations: [
				{ seq: 1, seconds: 120 },
				{ seq: 2, seconds: 120 },
			],
		},
	},
	/**
	 * Artistic gymnastics, Men's floor exercise final: D 5.4 + E 8.3 − 0.1
	 * → 13.600 (`result_value` 13.6, POINTS). The line-up example is one
	 * member's floor routine in the team final.
	 */
	JUDGED: {
		result: {
			parts: [
				{
					code: "FX",
					label: "Floor exercise",
					difficulty: 5.4,
					components: [{ code: "execution", value: 8.3 }],
					judges: [
						{ seat: "E1", value: 8.3 },
						{ seat: "E2", value: 8.2 },
						{ seat: "E3", value: 8.4 },
					],
					penalty: 0.1,
					total: 13.6,
					rank: 2,
				},
			],
		},
		lineup: { parts: [{ code: "FX", difficulty: 5.1, total: 13.2 }] },
		config: {
			panel: [
				{ seat: "D1", role: "DIFFICULTY" },
				{ seat: "E1", role: "EXECUTION" },
				{ seat: "E2", role: "EXECUTION" },
				{ seat: "E3", role: "EXECUTION" },
			],
			apparatus: ["FX"],
		},
		meetDetail: {},
	},
	/**
	 * Golf, Women's individual stroke play after round 2: 71 + 68 → 139
	 * (`result_value`, SCORE, lower is better). Round 1 shows its first three
	 * holes. The line-up example is a team member whose round 2 was discarded.
	 */
	ROUNDS: {
		result: {
			rounds: [
				{
					seq: 1,
					value: 71,
					rank: 4,
					parts: [
						{ seq: 1, value: 4 },
						{ seq: 2, value: 3 },
						{ seq: 3, value: 5 },
					],
				},
				{ seq: 2, value: 68, rank: 1 },
			],
			tallies: [{ code: "birdies", value: 9 }],
		},
		lineup: {
			rounds: [
				{ seq: 1, value: 72 },
				{ seq: 2, value: 75, discarded: true },
			],
		},
		config: { rounds: 4, discards: 1 },
		meetDetail: {},
	},
	/**
	 * Weightlifting, Men's 73 kg: snatch 145, clean & jerk 180 (the third
	 * attempt declared, not lifted yet) → total 325 (`result_value`, WEIGHT).
	 */
	LIFTS: {
		result: {
			lifts: [
				{ type: "SNATCH", seq: 1, weight_kg: 140, good: true },
				{ type: "SNATCH", seq: 2, weight_kg: 145, good: false },
				{ type: "SNATCH", seq: 3, weight_kg: 145, good: true },
				{ type: "CLEAN_JERK", seq: 1, weight_kg: 175, good: true },
				{ type: "CLEAN_JERK", seq: 2, weight_kg: 180, good: true },
				{ type: "CLEAN_JERK", seq: 3, weight_kg: 184, good: null },
			],
			bodyweight_kg: 72.85,
			lot_no: 7,
			entry_total_kg: 330,
		},
		lineup: {},
		config: {},
		meetDetail: {},
	},
	/** A sport not modelled yet — e.g. a demonstration event's route and grade. */
	GENERIC: {
		result: { lines: [{ label: "Route", value: "Lead, 8b+" }] },
		lineup: { lines: [{ label: "Role", value: "Belayer" }] },
		config: { lines: [{ label: "Format", value: "Two qualification routes" }] },
		meetDetail: { lines: [{ label: "Wall", value: "Wall B" }] },
	},
} satisfies { [F in ResultFamily]: FamilyExample<F> };

// ─── The headline fields ─────────────────────────────────────────────────────

/** `|result_value|` must be below this — `numeric(14, 4)` holds ten integer digits. */
export const RESULT_VALUE_LIMIT = 1e10;
export const RESULT_VALUE_DECIMALS = 4;
/** `|points|` must be below this — `numeric(10, 3)` holds seven integer digits. */
export const RESULT_POINTS_LIMIT = 1e7;
export const RESULT_POINTS_DECIMALS = 3;
/** The place within a meet, and a bracket source's rank. */
export const RESULT_RANK_MAX = 9999;
export const RESULT_DISPLAY_MAX = 60;

/**
 * True when `value` has at most `places` decimals AS A DOUBLE: `50.2345` has 4,
 * `0.1 + 0.2` (0.30000000000000004) has 17. Round-tripping through `toFixed`
 * asks exactly that, without the false negatives of `value * 10 ** places`
 * (50.2345 × 10000 is 502344.99999999994).
 */
const hasAtMostDecimals = (value: number, places: number): boolean => Number(value.toFixed(places)) === value;

/** `result_value`: finite, `|v| < 10^10`, at most 4 decimals — what `numeric(14, 4)` stores without rounding. */
export const resultValueField = z
	.number()
	.finite()
	.gt(-RESULT_VALUE_LIMIT)
	.lt(RESULT_VALUE_LIMIT)
	.refine((v) => hasAtMostDecimals(v, RESULT_VALUE_DECIMALS), `At most ${RESULT_VALUE_DECIMALS} decimals`);

/** `points`: finite, `|p| < 10^7`, at most 3 decimals — what `numeric(10, 3)` stores without rounding. */
export const resultPointsField = z
	.number()
	.finite()
	.gt(-RESULT_POINTS_LIMIT)
	.lt(RESULT_POINTS_LIMIT)
	.refine((v) => hasAtMostDecimals(v, RESULT_POINTS_DECIMALS), `At most ${RESULT_POINTS_DECIMALS} decimals`);

const uuidRef = () => z.string().uuid().toLowerCase();

/** Reports every repeat of a key in a list, at the repeating item. */
function refuseRepeats<T>(
	items: readonly T[],
	keyOf: (item: T) => string | null | undefined,
	ctx: z.RefinementCtx,
	path: (index: number) => (string | number)[],
	message: string,
): void {
	const seen = new Set<string>();
	items.forEach((item, index) => {
		const key = keyOf(item);
		if (key === null || key === undefined) return;
		if (seen.has(key)) ctx.addIssue({ code: z.ZodIssueCode.custom, path: path(index), message });
		seen.add(key);
	});
}

// ─── PUT /meets/:id/competitors — the start list ─────────────────────────────

export const START_LIST_SLOTS_MAX = 200;
export const START_LIST_ORDER_MAX = 999;
/** `lane` and `bib`. */
export const START_LIST_LABEL_MAX = 12;
/** A weigh-in, kilograms (`numeric(6,2)`: two decimals). Nobody who lifts weighs more. */
export const START_LIST_BODYWEIGHT_MAX = 400;

/**
 * Where an unfilled slot's competitor will come from — exactly one of
 *
 *   { meet_id, outcome: WINNER | LOSER }   "winner of QF1"
 *   { meet_id, rank }                      "2nd of Heat 3"
 *   { phase_id, rank, pool? }              "1st of Group A"
 *
 * — the table's CHECK constraints (`ck_meet_competitors_one_source`,
 * `_meet_source`, `_phase_source`) restated, so a bad shape is a 422 naming
 * the field instead of a 23514 from the database. The meet or phase must
 * belong to the same event; the composite foreign keys enforce that.
 */
export const slotSourceBody = z
	.object({
		meet_id: uuidRef().optional(),
		outcome: z.enum(SLOT_SOURCE_OUTCOMES).optional(),
		rank: z.number().int().min(1).max(RESULT_RANK_MAX).optional(),
		phase_id: uuidRef().optional(),
		pool: poolField.optional(),
	})
	.strict()
	.superRefine((s, ctx) => {
		const issue = (message: string) => ctx.addIssue({ code: z.ZodIssueCode.custom, message });
		if (s.meet_id !== undefined && s.phase_id !== undefined) return issue("A source is a meet OR a phase, not both");
		if (s.meet_id !== undefined) {
			if ((s.outcome === undefined) === (s.rank === undefined)) return issue("A meet source takes an outcome (WINNER / LOSER) or a rank — exactly one");
			if (s.pool !== undefined) return issue("A pool belongs to a phase source");
			return;
		}
		if (s.phase_id !== undefined) {
			if (s.rank === undefined) return issue("A phase source needs a rank");
			if (s.outcome !== undefined) return issue("An outcome belongs to a meet source");
			return;
		}
		issue("A source needs a meet_id or a phase_id");
	});

/**
 * One slot of the start list.
 *
 * Exactly one of `competitor_id` (who) and `source` (where they will come
 * from). Which existing slot an entry updates:
 *
 *   · `meet_competitor_id` given — that slot (created with that id if new);
 *   · else `competitor_id` — the slot that competitor already holds here;
 *   · else `source` — the unfilled slot with the same source;
 *   · otherwise a new slot.
 *
 * so the same call twice — with or without ids — changes nothing.
 *
 * Sending `competitor_id` for a slot that has a source keeps the source: a
 * bracket slot that the source's result filled stays wired to it.
 *
 * Absent and `null` mean the same for the start-list fields: this is a PUT,
 * and the slot becomes exactly what is sent.
 */
export const startListSlotBody = z
	.object({
		/** A client-made id for a new slot, or the id of an existing one. */
		meet_competitor_id: clientIdField.nullable(),
		competitor_id: uuidRef().nullable().optional(),
		source: slotSourceBody.nullable().optional(),
		start_order: z.number().int().min(1).max(START_LIST_ORDER_MAX).nullable().optional(),
		/** Lane, boat, board, target — trimmed, 1–12. Text: not every sport numbers them. */
		lane: z.string().trim().min(1).max(START_LIST_LABEL_MAX).nullable().optional(),
		/** HOME / AWAY, RED / BLUE — any case in, stored upper-case (`SIDE_PATTERN`). */
		side: sideField.nullable().optional(),
		/** This meet's bib, when it differs from the competitor's — trimmed, 1–12. Weightlifting's lot number. */
		bib: z.string().trim().min(1).max(START_LIST_LABEL_MAX).nullable().optional(),
		/** The entry for THIS meet ("325", "1:52.04") — weightlifting's entry total. Text, trimmed, 1–50 (as the competitor's `entry_mark`). */
		entry_mark: z.string().trim().min(1).max(COMPETITOR_ENTRY_MARK_MAX).nullable().optional(),
		/** Weighed in for THIS meet — weightlifting. Kilograms, > 0, at most two decimals. */
		bodyweight_kg: z.number().finite().positive().max(START_LIST_BODYWEIGHT_MAX).multipleOf(0.01).nullable().optional(),
	})
	.strict()
	.superRefine((s, ctx) => {
		const hasCompetitor = s.competitor_id !== undefined && s.competitor_id !== null;
		const hasSource = s.source !== undefined && s.source !== null;
		if (hasCompetitor === hasSource) {
			ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["competitor_id"], message: "Give a competitor_id or a source — exactly one" });
		}
	});

/** The start list, whole: slots not listed are removed. 0–200 slots. */
export const startListBody = z
	.object({
		/** The MEET's version, as loaded. */
		version: versionField,
		slots: z.array(startListSlotBody).max(START_LIST_SLOTS_MAX),
	})
	.strict()
	.superRefine((body, ctx) => {
		const at = (field: string) => (i: number) => ["slots", i, field];
		refuseRepeats(body.slots, (s) => s.meet_competitor_id, ctx, at("meet_competitor_id"), "This slot is listed twice");
		refuseRepeats(body.slots, (s) => s.competitor_id, ctx, at("competitor_id"), "A competitor holds one slot in a meet");
		refuseRepeats(
			body.slots,
			(s) => (s.source && !s.competitor_id ? JSON.stringify([s.source.meet_id, s.source.outcome, s.source.rank, s.source.phase_id, s.source.pool]) : null),
			ctx,
			at("source"),
			"Two slots cannot come from the same source",
		);
	});
export type StartListBody = z.input<typeof startListBody>;

/** Where an unfilled slot's competitor will come from. Every key is present; the ones the source does not use are null. */
export interface StartListSource {
	meet_id: string | null;
	outcome: SlotSourceOutcome | null;
	rank: number | null;
	phase_id: string | null;
	pool: string | null;
}

export interface StartListSlot {
	meet_competitor_id: string;
	/** The SLOT's version — what a result or line-up save sends back. */
	version: number;
	/** null while the slot waits for its source. */
	competitor_id: string | null;
	/** null for a slot that was never wired to a source. */
	source: StartListSource | null;
	start_order: number | null;
	lane: string | null;
	side: string | null;
	bib: string | null;
	entry_mark: string | null;
	bodyweight_kg: number | null;
}

/** `PUT /meets/:id/competitors`. `slots` in the order they were sent. */
export interface StartListResponse {
	/** Unchanged when the call changed nothing. */
	meet_version: number;
	slots: StartListSlot[];
}

/**
 * `details` of the 409 CONFLICT when a start list is refused. Nothing was
 * written. `index` is the slot's position in the request's `slots`.
 */
export interface StartListConflictDetails {
	field: "slots";
	/** The id names a slot of ANOTHER meet. */
	foreign_slots: { index: number; meet_competitor_id: string }[];
	/** Slots the call would remove that already hold a result. */
	removed_with_result: { meet_competitor_id: string }[];
	/** Slots whose competitor would change while they hold a result or a line-up. */
	competitor_change_blocked: { index: number; meet_competitor_id: string; has_result: boolean; has_lineup: boolean }[];
	/** Competitors that are not entered in this meet's event. */
	unknown_competitors: { index: number; competitor_id: string }[];
	/** Competitors being added whose entry is not ENTERED. */
	not_entered: { index: number; competitor_id: string; status: CompetitorStatus }[];
	/** A source meet or phase that is not part of this event (or is this meet itself). */
	invalid_sources: { index: number; meet_id: string | null; phase_id: string | null }[];
	/**
	 * A competitor moving from one slot that STAYS to another. One statement
	 * cannot move it without briefly holding two slots of the meet; change
	 * the slots' lanes instead, or leave the old slot out of the list.
	 */
	moved: { index: number; competitor_id: string; from_meet_competitor_id: string }[];
	/**
	 * The slot that won the meet on a BYE, when the list would give the meet
	 * any other slot: a bye is the meet's only slot. Remove the bye first.
	 */
	bye_blocked: { meet_competitor_id: string }[];
}

// ─── PUT /meets/:id/settings ─────────────────────────────────────────────────

/**
 * A meet's `config` and / or `detail`, validated with the meet's effective
 * family (`meetConfigSchema`, `meetDetailSchema`) by the API, which knows the
 * family. A key that is absent leaves that column alone; each object sent is
 * stored whole. At least one of the two.
 *
 * Refused, nothing written: a locked event (409 `{ locked: true }`); an
 * OFFICIAL meet (409 `{ official: true }`, like its results — `best_of`,
 * `attempts_allowed`, a wind reading are part of the result, frozen until the
 * meet is reopened); a family that changed after validation (409
 * STALE_VERSION `{ result_family }`); another meet `version` (409
 * STALE_VERSION). A `config` or `detail` that is not `{}` makes the meet hold
 * results: its result family, type and direction are frozen from then on.
 */
export const meetSettingsBody = z
	.object({
		/** The MEET's version, as loaded. */
		version: versionField,
		config: z.object({}).passthrough().optional(),
		detail: z.object({}).passthrough().optional(),
	})
	.strict()
	.refine((b) => b.config !== undefined || b.detail !== undefined, { message: "Send config, detail or both", path: ["config"] });
export type MeetSettingsBody = z.input<typeof meetSettingsBody>;

/** `PUT /meets/:id/settings`. */
export interface MeetSettingsResponse {
	meet_id: string;
	/** Unchanged when the call changed nothing. */
	version: number;
	config: FamilyMeetConfig;
	detail: FamilyMeetDetail;
}

// ─── PATCH /meets/:id/results ────────────────────────────────────────────────

export const RESULTS_SAVE_MAX = 200;

/**
 * One slot's result. A key that is ABSENT is left alone; a key sent as `null`
 * clears it (`detail: null` resets to `{}`, `records: null` to `[]`). That is
 * what lets two operators save different rows — or different fields of one
 * row — without overwriting each other.
 *
 * `detail` is checked here only for being an object: its shape depends on the
 * meet's family, which the API reads and applies (`resultDetailSchema`).
 */
export const resultRowBody = z
	.object({
		meet_competitor_id: uuidRef(),
		/** The SLOT's version, as loaded. */
		version: versionField,
		irm: z.enum(IRM_CODES).nullable().optional(),
		result_value: resultValueField.nullable().optional(),
		result_display: z.string().trim().min(1).max(RESULT_DISPLAY_MAX).nullable().optional(),
		points: resultPointsField.nullable().optional(),
		rank: z.number().int().min(1).max(RESULT_RANK_MAX).nullable().optional(),
		outcome: z.enum(MEET_OUTCOMES).nullable().optional(),
		records: z
			.array(z.string().trim().regex(RESULT_RECORD_CODE_PATTERN))
			.max(RESULT_RECORDS_MAX)
			.superRefine((codes, ctx) => refuseRepeats(codes, (c) => c, ctx, (i) => [i], "Listed twice"))
			.nullable()
			.optional(),
		detail: z.object({}).passthrough().nullable().optional(),
	})
	.strict();

/** Results for 1–200 slots of one meet, saved all together or not at all. */
export const saveResultsBody = z
	.object({
		results: z.array(resultRowBody).min(1).max(RESULTS_SAVE_MAX),
	})
	.strict()
	.superRefine((body, ctx) =>
		refuseRepeats(body.results, (r) => r.meet_competitor_id, ctx, (i) => ["results", i, "meet_competitor_id"], "This slot is listed twice"),
	);
export type SaveResultsBody = z.input<typeof saveResultsBody>;

/** A slot and its version — what a save returns, and what a stale save reports. */
export interface SlotVersion {
	meet_competitor_id: string;
	version: number;
}

/** `PATCH /meets/:id/results`: every row's new version, in the order sent. */
export interface SaveResultsResponse {
	saved: SlotVersion[];
}

/**
 * `DELETE /meets/:id/results?reason=`: every slot's result and line-up
 * detail wiped, a LIVE / UNOFFICIAL meet back to START_LIST. The start list
 * stays. Refused for an OFFICIAL meet (reopen first), a locked event, or a
 * medal on one of its slots (409 CONFLICT `{ in_use: "medals" }`).
 */
export interface ClearMeetResultsResponse {
	meet_id: string;
	/** The meet's new version (unchanged when there was nothing to clear). */
	version: number;
	/** Slots whose result was wiped. */
	cleared: number;
	/** Competitors this meet had sent to a later phase who were taken back out of it (mark cleared, later entries removed). */
	unadvanced: number;
	/** Competitors left in a later phase because they already hold a slot there — remove them from that start list first. */
	still_advanced: number;
}

/**
 * `details` of the 409 STALE_VERSION when a save is refused. Nothing was
 * saved — not even the rows that were current.
 *
 * A save refused because the meet's result FAMILY changed after the API
 * validated the rows' `detail` answers 409 STALE_VERSION with
 * `{ result_family: <the current family> }` instead: reload the whole meet.
 */
export interface StaleResultsDetails {
	/** Rows whose version moved on; `version` is the CURRENT one. Reload these. */
	stale: SlotVersion[];
	/** Ids that are not a filled slot of this meet. */
	not_found: string[];
}

// ─── PUT /meet-competitors/:id/lineup ────────────────────────────────────────

export const LINEUP_MEMBERS_MAX = 40;
/** Relay leg, board number, batting order. */
export const LINEUP_SORT_ORDER_MAX = 99;
export const LINEUP_POSITION_MAX = 20;

/**
 * One member of the slot's team in this meet's line-up. The member must be on
 * THAT competitor's roster — the database's composite foreign key refuses
 * anyone else. `detail` is validated by the API with the meet's family
 * (`lineupDetailSchema`).
 *
 * This is a PUT: an absent `sort_order` / `position` is stored empty,
 * `is_starter` defaults to true, `is_captain` to false, `detail` to `{}`.
 */
export const lineupMemberBody = z
	.object({
		competitor_member_id: uuidRef(),
		sort_order: z.number().int().min(1).max(LINEUP_SORT_ORDER_MAX).nullable().optional(),
		/** Playing position — "GK", "Setter". Trimmed, ≤ 20. */
		position: z.string().trim().min(1).max(LINEUP_POSITION_MAX).nullable().optional(),
		is_starter: z.boolean().optional(),
		is_captain: z.boolean().optional(),
		detail: z.object({}).passthrough().nullable().optional(),
	})
	.strict();

/**
 * The slot's whole line-up, 0–40 members: members not listed are taken out of
 * it. Refused like a results save: a locked event, an OFFICIAL meet, a family
 * that changed after validation (409 STALE_VERSION `{ result_family }`), a
 * stale slot `version`. A member's `detail` that is not `{}` makes the meet
 * hold results (its result settings are frozen).
 */
export const lineupBody = z
	.object({
		/** The SLOT's version, as loaded. */
		version: versionField,
		lineup: z.array(lineupMemberBody).max(LINEUP_MEMBERS_MAX),
	})
	.strict()
	.superRefine((body, ctx) =>
		refuseRepeats(body.lineup, (m) => m.competitor_member_id, ctx, (i) => ["lineup", i, "competitor_member_id"], "This member is listed twice"),
	);
export type LineupBody = z.input<typeof lineupBody>;

/** One line-up row, with the roster facts a scoring form shows. A person is an id and a printed name — nothing more. */
export interface SlotLineupEntry {
	meet_lineup_id: string;
	competitor_member_id: string;
	people_id: string;
	name: string;
	/** From the roster. */
	role: MemberRole;
	/** From the roster. */
	bib: string | null;
	sort_order: number | null;
	position: string | null;
	is_starter: boolean;
	is_captain: boolean;
	detail: FamilyLineupDetail;
}

/** `PUT /meet-competitors/:id/lineup`. `lineup` ordered by `sort_order` (empty last), then roster order. */
export interface SlotLineupResponse {
	meet_competitor_id: string;
	/** The slot's version — unchanged when the call changed nothing. */
	version: number;
	lineup: SlotLineupEntry[];
}

/** `details` of the 409 CONFLICT when a line-up names someone outside the slot's roster. */
export interface LineupConflictDetails {
	field: "lineup";
	/** The first offender — what a form highlights. */
	competitor_member_id: string;
	/** All of them, in the order sent. */
	competitor_member_ids: string[];
}
